import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, FailureNotice, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData, type AsyncState } from '@/hooks';
import { GROUP_MEMBERSHIP_ROLES, institutionDisplayName } from '@/types/platform';
import type {
  GroupMembershipRole,
  InstitutionGroupMember,
  PlatformInstitutionRecord,
} from '@/types/platform';
import { formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * The institutions tab — who sits in this group, and what role they sit in.
 *
 * ROLES ARE THE SERVER'S VOCABULARY, AND HEAD_OFFICE IS THE ONE WITH TEETH.
 * Promoting a member to HEAD_OFFICE demotes whoever holds it now: core enforces
 * exactly one, and invoicing follows the designation, so the promotion goes
 * through the confirmation dialog with the demotion stated out loud. Every
 * other role change applies immediately — MEMBER, SUBSIDIARY and AFFILIATE are
 * ordinary seats, and a dialog for each would make the table unusable.
 *
 * THE CONFIRMATION REASON IS ASKED FOR AND NOT SENT. Neither the remove route
 * nor the change-role route carries a reason field — their bodies are empty or
 * `{ role }` — yet ConfirmActionDialog always asks for one, and that is not a
 * waste: the act of writing down what is happening is the part that stops a
 * misclick, and core records the acting identity itself. What is dropped is the
 * character count, not the thought; the reason stays with the person who typed
 * it, as the contract intends.
 */
export function GroupMembersPanel({
  groupId,
  membersState,
  onRetry,
}: {
  groupId: string;
  membersState: AsyncState<InstitutionGroupMember[]>;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState<InstitutionGroupMember | null>(null);
  const [promoting, setPromoting] = useState<{ member: InstitutionGroupMember; role: GroupMembershipRole } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const changeRole = async (member: InstitutionGroupMember, role: GroupMembershipRole) => {
    setBusyId(member.id);
    setRowError(null);
    try {
      await platformApi.structure.changeGroupMemberRole(groupId, member.institutionId, role);
      onRetry();
    } catch (cause) {
      setRowError((cause as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (member: InstitutionGroupMember) => {
    setBusyId(member.id);
    setRowError(null);
    try {
      await platformApi.structure.removeGroupMember(groupId, member.institutionId);
      setRemoving(null);
      onRetry();
    } catch (cause) {
      setRowError((cause as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Panel
        title={t('platform.groups.members.title')}
        icon="institution"
        actions={
          <button type="button" className={styles.primaryBtnSm} onClick={() => setAddOpen(true)}>
            <Icon name="plus" size={15} />
            {t('platform.groups.members.add')}
          </button>
        }
      >
        <AsyncPanel
          state={membersState}
          onRetry={onRetry}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.groups.members.emptyTitle')}
          emptyBody={t('platform.groups.members.emptyBody')}
        >
          {(data) => (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <caption className={styles.tableCaption}>
                    {t('platform.groups.members.tableCaption', { n: data.length })}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('platform.groups.members.colInstitution')}</th>
                      <th scope="col">{t('platform.groups.members.colRole')}</th>
                      <th scope="col">{t('platform.groups.members.colJoined')}</th>
                      <th scope="col">{t('platform.groups.members.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((member) => (
                      <tr key={member.id}>
                        <th scope="row" className={styles.tableRowHeader}>
                          <Link
                            className={styles.rowTitleLink}
                            to={`/admin/institutions/${encodeURIComponent(member.institutionId)}`}
                          >
                            {member.institutionName ?? member.institutionId}
                          </Link>
                          {member.role === 'HEAD_OFFICE' && (
                            <span className={styles.okTag}>{t('platform.structure.headOffice')}</span>
                          )}
                        </th>
                        <td>
                          <Select
                            aria-label={t('platform.groups.members.roleAria')}
                            value={member.role}
                            disabled={busyId === member.id}
                            onChange={(value) => {
                              const role = value as GroupMembershipRole;
                              if (role === 'HEAD_OFFICE' && member.role !== 'HEAD_OFFICE') {
                                setPromoting({ member, role });
                              } else {
                                changeRole(member, role);
                              }
                            }}
                            options={GROUP_MEMBERSHIP_ROLES.map((role) => ({
                              value: role,
                              label: t(`platform.structure.membership.${role}`, { defaultValue: role }),
                            }))}
                          />
                        </td>
                        <td className={styles.mutedCell}>
                          {member.joinedAt ? formatDateTime(member.joinedAt, locale) : '—'}
                        </td>
                        <td>
                          <button
                            type="button"
                            className={styles.linkBtnDanger}
                            disabled={busyId === member.id}
                            onClick={() => setRemoving(member)}
                          >
                            {t('platform.groups.members.remove')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {rowError && (
                <p className={styles.formError} role="alert">
                  {rowError}
                </p>
              )}
            </>
          )}
        </AsyncPanel>
      </Panel>

      {removing && (
        <ConfirmActionDialog
          open
          title={t('platform.groups.members.removeTitle', {
            name: removing.institutionName ?? removing.institutionId,
          })}
          intro={t('platform.groups.members.removeIntro')}
          tone="critical"
          impactStatus="ready"
          impact={
            <ul className={styles.impactList}>
              <li>{t('platform.groups.members.removeImpactSeat')}</li>
              <li>{t('platform.groups.members.removeImpactUntouched')}</li>
            </ul>
          }
          confirmLabel={t('platform.groups.members.remove')}
          submitting={busyId === removing.id}
          error={rowError}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            remove(removing);
            setRemoving(null);
          }}
        />
      )}

      {promoting && (
        <ConfirmActionDialog
          open
          title={t('platform.groups.members.promoteTitle', {
            name: promoting.member.institutionName ?? promoting.member.institutionId,
          })}
          intro={t('platform.groups.members.promoteIntro')}
          tone="warning"
          impactStatus="ready"
          impact={
            <ul className={styles.impactList}>
              <li>{t('platform.groups.members.promoteImpactMove')}</li>
              <li>{t('platform.groups.members.promoteImpactInvoicing')}</li>
            </ul>
          }
          confirmLabel={t('platform.groups.members.promote')}
          submitting={busyId === promoting.member.id}
          error={rowError}
          onCancel={() => setPromoting(null)}
          onConfirm={() => {
            const { member, role } = promoting;
            changeRole(member, role);
            setPromoting(null);
          }}
        />
      )}

      <AddMemberDialog
        groupId={groupId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={onRetry}
      />
    </>
  );
}

function AddMemberDialog({
  groupId,
  open,
  onClose,
  onAdded,
}: {
  groupId: string;
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const fetcher = useCallback(() => platformApi.institutions.list(), []);
  const { state, reload } = useAsyncData<PlatformInstitutionRecord[]>('group-add-register', fetcher, open);

  const [institutionId, setInstitutionId] = useState('');
  const [role, setRole] = useState<GroupMembershipRole>('MEMBER');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!institutionId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.structure.addGroupMember(groupId, institutionId, role);
      setInstitutionId('');
      onAdded();
      onClose();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.groups.members.addTitle')}
      description={t('platform.groups.members.addLead')}
      closeOnBackdropClick={false}
    >
      {state.status === 'loading' ? (
        <p className={styles.sectionEmpty} role="status">
          {t('common.loading')}
        </p>
      ) : state.status === 'error' ? (
        <>
          <p className={styles.dataNotice}>
            <Icon name="info" size={15} aria-hidden="true" />
            {t('platform.institutions.registryMissing')}
          </p>
          <FailureNotice error={state.error} onRetry={reload} />
        </>
      ) : (
        <form className={styles.dialogForm} onSubmit={submit} noValidate>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.groups.members.fieldInstitution')}</span>
            <Select
              searchable
              searchPlaceholder={t('platform.groups.members.searchPlaceholder')}
              emptyLabel={t('platform.groups.members.noMatch')}
              value={institutionId}
              onChange={setInstitutionId}
              placeholder={t('platform.groups.members.institutionPlaceholder')}
              options={state.data.map((institution) => ({
                value: institution.id,
                label: institutionDisplayName(institution),
                description: institution.code,
              }))}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.groups.members.fieldRole')}</span>
            <Select
              value={role}
              onChange={(value) => setRole(value as GroupMembershipRole)}
              options={GROUP_MEMBERSHIP_ROLES.map((candidate) => ({
                value: candidate,
                label: t(`platform.structure.membership.${candidate}`, { defaultValue: candidate }),
              }))}
            />
            <span className={styles.fieldHint}>{t('platform.groups.members.fieldRoleHint')}</span>
          </label>

          {error && (
            <p className={styles.formError} role="alert">
              {error}
            </p>
          )}

          <div className="modal-actions">
            <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="modal-button" disabled={!institutionId || submitting}>
              {submitting ? t('platform.confirm.submitting') : t('platform.groups.members.add')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
